import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { sanityClient } from "../lib/sanityClient";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const VehicleDetailsPage = () => {
  const { categorySlug, vehicleSlug, lang } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainImage, setMainImage] = useState("");

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const query = `
          *[_type == "vehicle" && slug.current == $vehicleSlug && 
            categories[0]->slug.current == $categorySlug][0]{
              _id,
              title,
              description,
              price,
              condition,
              brand,
              model,
              year,
              mileage,
              slug,
              primaryImage,
              additionalImages,
              categories[]->{slug}
          }
        `;
        const data = await sanityClient.fetch(query, { vehicleSlug, categorySlug });
        setVehicle(data);
        setMainImage(data?.primaryImage);
      } catch (error) {
        console.error("Sanity Fetch Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicle();
  }, [vehicleSlug, categorySlug]);

  if (loading) return <p className="text-center text-gray-600">Loading vehicle details...</p>;
  if (!vehicle) return <p className="text-center text-red-500">Vehicle not found.</p>;

  // Fallback for localized fields
  const getLocalizedText = (field) => field?.[lang] || field?.en || "—";

  const additionalImages = Array.isArray(vehicle.additionalImages)
    ? vehicle.additionalImages.filter(Boolean)
    : [];

  const handleImageClick = (image) => {
    setMainImage(image);
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: additionalImages.length < 3 ? additionalImages.length : 3,
    slidesToScroll: 1,
    arrows: true,
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="max-w-5xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Section - Images */}
        <div className="flex flex-col items-center">
          <div className="w-full mb-4">
            <img
              src={mainImage}
              alt="Selected Vehicle"
              className="w-full h-96 object-cover rounded-lg shadow-md transition-all duration-300"
            />
          </div>

          {/* Carousel Thumbnails */}
          {additionalImages.length > 0 && (
            <Slider {...settings} className="w-full">
              {[vehicle.primaryImage, ...additionalImages].map((img, index) => (
                <div key={index} className="px-2">
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className={`w-full h-28 object-cover rounded-md shadow-md cursor-pointer transition-all duration-300 ${
                      img === mainImage ? "border-4 border-blue-500" : "hover:opacity-80"
                    }`}
                    onClick={() => handleImageClick(img)}
                  />
                </div>
              ))}
            </Slider>
          )}
        </div>

        {/* Right Section - Details */}
        <div className="p-4">
          <h1 className="text-3xl font-bold text-gray-800 mb-3">
            {getLocalizedText(vehicle.title)}
          </h1>
          <p className="text-gray-600 text-lg mb-4">
            {getLocalizedText(vehicle.description)}
          </p>

          <div className="grid grid-cols-2 gap-4 text-gray-700">
            <p><span className="font-semibold">Price:</span> {vehicle.price} Million CFA</p>
            <p><span className="font-semibold">Condition:</span> {vehicle.condition}</p>
            <p><span className="font-semibold">Brand:</span> {vehicle.brand}</p>
            <p><span className="font-semibold">Model:</span> {vehicle.model}</p>
            <p><span className="font-semibold">Year:</span> {vehicle.year}</p>
            <p><span className="font-semibold">Mileage:</span> {vehicle.mileage} KM</p>
          </div>

          <div className="mt-6">
            <Link
              to={`/cm/${lang}/collections/${categorySlug}`}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-300"
            >
              Back to Category
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetailsPage;
